import { NgModule } from '@angular/core';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';

import { CompaniesStoreModule } from '@companies/store/companies-store.module';
import * as fromDashboardFilterTransient from '@dashboards/store';
import { DashboardEffects } from '@dashboards/store/effects/dashboard.effects';
import { KpiEffects } from '@dashboards/store/effects/kpi.effects';
import * as fromDashboardFilterReducer from '@dashboards/store/reducers/dashboard-filter.reducer';
import { metaDashboardFilterReducers } from '@dashboards/store/reducers/dashboard-filter.reducer';
import * as fromDashboardsReducer from '@dashboards/store/reducers/dashboards.reducer';
import { metaDashboardReducers } from '@dashboards/store/reducers/dashboards.reducer';
import * as fromDashboardKpiReducer from '@dashboards/store/reducers/kpi.reducer';
import { metaDashboardKpiReducers } from '@dashboards/store/reducers/kpi.reducer';
import {
  metaPropertySelectorReducers,
  propertySelectorFeatureKey,
  propertySelectorReducer,
} from '@dashboards/store/reducers/property-selector.reducer';
import { AdministrationEffects } from '@main-application/administration/store/effects/administration.effects';
import * as fromAdministration from '@main-application/administration/store/reducers/administration.reducer';
import { BoardsStoreModule } from '@main-application/boards/store/boards-store.module';
import { CalendarStoreModule } from '@main-application/calendar/store/calendar-store.module';
import { DelinquencyEffects } from '@main-application/dashboard-reports/delinquencies/store/delinquency.effects';
import * as fromDelinquencyReducer from '@main-application/dashboard-reports/delinquencies/store/delinquency.reducer';
import { RenewalsEffects } from '@main-application/dashboard-reports/renewals/store/renewals.effects';
import * as fromRenewalsReducer from '@main-application/dashboard-reports/renewals/store/renewals.reducer';
import { RocReportStoreModule } from '@main-application/dashboard-reports/roc-report/store/roc-store.module';
import { InspectionStoreModule } from '@main-application/inspections/store/inspections-store.module';
import * as fromStoreReducer from '@main-application/management/pages/service-configuration/store/service-configuration.reducer';
import { SystemConfigurationEffects } from '@main-application/management/pages/system-configuration/store/system-configuration.effects';
import * as fromSystemConfiguration from '@main-application/management/pages/system-configuration/store/system-configuration.reducer';
import { TurnoverConfigurationStepStoreModule } from '@main-application/management/pages/turnover-configuration/components/turnover-configuration-step-assignees/store/turnover-configuration-step-store.module';
import { TurnoverConfigurationStoreModule } from '@main-application/management/pages/turnover-configuration/store/turnover-configuration-store.module';
import { ManagementEffects } from '@main-application/management/store/management.effects';
import * as fromManagement from '@main-application/management/store/management.reducer';
import { NotificationsEffects } from '@main-application/notifications/store/effects/notifications.effects';
import * as fromNotifications from '@main-application/notifications/store/reducers/notifications.reducer';
import { ServiceStoreModule } from '@main-application/service/store/service-store.module';
import { EnumerationEffects } from '@main-application/store/effects/enumeration.effects';
import { FileUploadEffects } from '@main-application/store/effects/file-upload.effects';
import { UserEffects } from '@main-application/store/effects/user.effects';
import { CORE_FEATURE_KEY, reducers } from '@main-application/store/reducers';
import { TicketsEffects } from '@main-application/tickets/store/effects/tickets.effects';
import * as fromTickets from '@main-application/tickets/store/reducers/tickets.reducer';
import { TurnoverListEffects } from '@main-application/turnovers/components/turnover-list/store/effects/turnover-list.effects';
import * as fromTurnoverListReducer from '@main-application/turnovers/components/turnover-list/store/reducers/turnover-list.reducer';
import { metaTurnoverListReducers } from '@main-application/turnovers/components/turnover-list/store/reducers/turnover-list.reducer';
import { BulkBidsEffects } from '@main-application/turnovers/store/effects/bulk-bids.effects';
import { PunchEffects } from '@main-application/turnovers/store/effects/punch.effects';
import { TurnoverBidsEffects } from '@main-application/turnovers/store/effects/turnover-bids.effects';
import { TurnoversEffects } from '@main-application/turnovers/store/effects/turnovers.effects';
import * as fromBulkBids from '@main-application/turnovers/store/reducers/bulk-bids.reducer';
import * as fromPunch from '@main-application/turnovers/store/reducers/punch.reducer';
import {
  turnoverBidsFeatureKey,
  turnoverBidsReducer,
} from '@main-application/turnovers/store/reducers/turnover-bids.reducer';
import {
  turnoverOverviewFeatureKey,
  turnoverOverviewReducer,
} from '@main-application/turnovers/store/reducers/turnovers-overview.reducer';
import * as fromTurnovers from '@main-application/turnovers/store/reducers/turnovers.reducer';
import { PortfolioStoreModule } from '@portfolio/store/portfolio-store.module';
import { TemplateStoreModule } from '@template/store/template-store.module';

@NgModule({
  exports: [],
  imports: [
    BoardsStoreModule,
    CalendarStoreModule,
    PortfolioStoreModule,
    RocReportStoreModule,
    TurnoverConfigurationStoreModule,
    TurnoverConfigurationStepStoreModule,
    InspectionStoreModule,
    TemplateStoreModule,
    CompaniesStoreModule,
    ServiceStoreModule,
    EffectsModule.forFeature([
      DashboardEffects,
      KpiEffects,
      AdministrationEffects,
      TurnoversEffects,
      BulkBidsEffects,
      PunchEffects,
      SystemConfigurationEffects,
      TurnoverBidsEffects,
      DelinquencyEffects,
      RenewalsEffects,
      ManagementEffects,
      NotificationsEffects,
      TicketsEffects,
      TurnoverListEffects,
      UserEffects,
      EnumerationEffects,
      FileUploadEffects,
    ]),
    StoreModule.forFeature(fromSystemConfiguration.systemConfigurationFeatureKey, fromSystemConfiguration.reducer),
    StoreModule.forFeature(fromDashboardsReducer.dashboardsFeatureKey, fromDashboardsReducer.reducer, {
      metaReducers: metaDashboardReducers,
    }),
    StoreModule.forFeature(fromDashboardKpiReducer.kpiFeatureKey, fromDashboardKpiReducer.reducer, {
      metaReducers: metaDashboardKpiReducers,
    }),
    StoreModule.forFeature(fromDashboardFilterReducer.dashboardFilterFeatureKey, fromDashboardFilterReducer.reducer, {
      metaReducers: metaDashboardFilterReducers,
    }),
    StoreModule.forFeature(fromAdministration.administrationFeatureKey, fromAdministration.reducer),
    StoreModule.forFeature(fromTurnovers.turnoversFeatureKey, fromTurnovers.reducer),
    StoreModule.forFeature(fromBulkBids.bulkBidsFeatureKey, fromBulkBids.reducer),
    StoreModule.forFeature(fromPunch.punchFeatureKey, fromPunch.reducer),
    StoreModule.forFeature(
      fromDashboardFilterTransient.dashboardFilterTransientFeatureKey,
      fromDashboardFilterTransient.reducer
    ),
    StoreModule.forFeature(turnoverBidsFeatureKey, turnoverBidsReducer),
    StoreModule.forFeature(fromDelinquencyReducer.delinquencyFeatureKey, fromDelinquencyReducer.reducer),
    StoreModule.forFeature(fromRenewalsReducer.renewalsFeatureKey, fromRenewalsReducer.reducer),
    StoreModule.forFeature(fromStoreReducer.serviceConfigurationFeatureKey, fromStoreReducer.reducer),
    StoreModule.forFeature(fromManagement.managementFeatureKey, fromManagement.reducer),
    StoreModule.forFeature(fromNotifications.notificationsFeatureKey, fromNotifications.reducer),
    StoreModule.forFeature(fromTickets.ticketsFeatureKey, fromTickets.reducer),
    StoreModule.forFeature(fromTurnoverListReducer.turnoverListFeatureKey, fromTurnoverListReducer.reducer, {
      metaReducers: metaTurnoverListReducers,
    }),
    StoreModule.forFeature(turnoverOverviewFeatureKey, turnoverOverviewReducer),
    StoreModule.forFeature(CORE_FEATURE_KEY, reducers),
    StoreModule.forFeature(propertySelectorFeatureKey, propertySelectorReducer, {
      metaReducers: metaPropertySelectorReducers,
    }),
  ],
  declarations: [],
})
export class SharedStoreModule {}
